/*
 * PLAYXWORLD LAYOUT — MONOCHROME BRUTALISM
 * Dark theme, thick borders, solid shadows, Space Grotesk headings
 * Sticky top nav with marquee announcement bar + User Profile Dropdown
 * Auth: Firebase Auth + LINE Login (PlayX ID)
 */
import { Link, useLocation } from "wouter";
import { useState, useRef, useEffect } from "react";
import { Menu, X, Gamepad2, Zap, HelpCircle, Info, User, LogOut, CreditCard, Wallet } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useUnifiedAuth } from "@/contexts/UnifiedAuthContext";
import { isMausAuth } from "@/lib/envDetection";
import { trpc } from "@/lib/trpc";
import LanguageSwitcher from "./LanguageSwitcher";

const navLinks = [
  { href: "/", labelKey: "nav.home", icon: Zap },
  { href: "/games", labelKey: "nav.games", icon: Gamepad2 },
  { href: "/topup", labelKey: "nav.topup", icon: Zap },
  { href: "/support", labelKey: "nav.support", icon: HelpCircle },
  { href: "/about", labelKey: "nav.about", icon: Info },
];

// LINE Login SVG Icon
function LineIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  );
}

function UserDropdown() {
  const { user, isAuthenticated, logout, loading } = useUnifiedAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Get LINE Login URL from server (only on Manus envs where the route exists)
  const { data: lineLoginData } = trpc.firebase.getLineLoginUrl.useQuery(
    { returnPath: window.location.pathname },
    { enabled: isMausAuth() && !isAuthenticated && !loading, retry: false }
  );

  // Get credit balance — only on Manus env (Firebase env reads it differently)
  const { data: creditData } = trpc.credit.balance.useQuery(undefined, {
    enabled: isMausAuth() && isAuthenticated,
    refetchInterval: 30000,
    retry: false,
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="w-24 h-9 border-2 border-white/30 animate-pulse" />
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <a
        href={lineLoginData?.url || "#"}
        className="btn-brutal bg-[#06C755] text-white text-xs py-2 px-4 inline-flex items-center gap-2 border-white hover:brightness-110 transition-all"
      >
        <LineIcon size={14} />
        LINE Login
      </a>
    );
  }

  const balance = creditData?.balance ?? user.creditBalance ?? "0.00";
  const profileImg = user.profileImage;
  const initial = (user.name || "U").charAt(0).toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border-2 border-white px-3 py-1.5 hover:bg-white hover:text-black transition-colors"
      >
        {profileImg ? (
          <img
            src={profileImg}
            alt={user.name || "User"}
            className="w-7 h-7 object-cover"
          />
        ) : (
          <div className="w-7 h-7 bg-white text-black flex items-center justify-center font-heading font-bold text-xs">
            {initial}
          </div>
        )}
        <span className="font-mono text-xs hidden sm:block max-w-[100px] truncate">
          {user.name || "User"}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-64 bg-black border-3 border-white z-50"
          >
            {/* User Info */}
            <div className="p-4 border-b-2 border-white/20">
              <div className="flex items-center gap-3">
                {profileImg ? (
                  <img
                    src={profileImg}
                    alt={user.name || "User"}
                    className="w-10 h-10 object-cover shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 bg-white text-black flex items-center justify-center font-heading font-bold text-lg shrink-0">
                    {initial}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{user.name || "User"}</div>
                  <div className="font-mono text-[10px] text-white/50 truncate">{user.email || ""}</div>
                  {user.lineUserId && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <LineIcon size={10} />
                      <span className="font-mono text-[9px] text-[#06C755]">PlayX ID Connected</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Points Balance */}
            <div className="p-4 border-b-2 border-white/20 bg-white/5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] tracking-widest text-white/50">PLAYXPOINT BALANCE</div>
                  <div className="font-heading font-bold text-xl mt-1">{parseFloat(balance).toLocaleString("th-TH", { minimumFractionDigits: 0 })} Points</div>
                </div>
                <Link
                  href="/playxpoint-history"
                  onClick={() => setOpen(false)}
                  className="border-2 border-white px-3 py-1.5 font-mono text-[10px] tracking-wider hover:bg-white hover:text-black transition-colors"
                >
                  VIEW
                </Link>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <Link
                href="/games"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
              >
                <Gamepad2 size={14} />
                <span className="font-mono text-xs">เติมเกม</span>
              </Link>
              <Link
                href="/orders"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
              >
                <CreditCard size={14} />
                <span className="font-mono text-xs">ประวัติการสั่งซื้อ</span>
              </Link>
            </div>

            {/* Logout */}
            <div className="border-t-2 border-white/20 p-2">
              <button
                onClick={() => { logout(); setOpen(false); }}
                className="flex items-center gap-3 px-4 py-2.5 w-full hover:bg-white/10 transition-colors text-white/60 hover:text-white"
              >
                <LogOut size={14} />
                <span className="font-mono text-xs">ออกจากระบบ</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Navbar() {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-50">
      {/* Marquee Announcement Bar */}
      <div className="bg-white text-black border-b-3 border-black overflow-hidden py-1.5">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(4)].map((_, i) => (
            <span key={i} className="mx-8 font-mono text-xs tracking-widest uppercase">
              {t("header.marquee")}
            </span>
          ))}
        </div>
      </div>

      {/* Main Nav */}
      <nav className="bg-black text-white border-b-3 border-white">
        <div className="container flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-white text-black flex items-center justify-center font-heading font-bold text-lg border-2 border-white">
              PX
            </div>
            <span className="font-heading font-bold text-xl tracking-tight hidden sm:block">
              PLAY<span className="text-white/60">X</span>WORLD
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location === link.href || (link.href !== "/" && location.startsWith(link.href));
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`font-heading font-semibold text-sm tracking-wider px-4 py-2 transition-all border-2 ${
                    isActive
                      ? "bg-white text-black border-white"
                      : "border-transparent hover:border-white"
                  }`}
                >
                  {t(link.labelKey)}
                </Link>
              );
            })}
          </div>

          {/* User Profile + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <UserDropdown />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center border-2 border-white"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden border-t-2 border-white overflow-hidden"
            >
              <div className="container py-4 flex flex-col gap-2">
                {navLinks.map((link) => {
                  const isActive = location === link.href || (link.href !== "/" && location.startsWith(link.href));
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className={`font-heading font-semibold text-sm tracking-wider px-4 py-3 border-2 flex items-center gap-3 ${
                        isActive
                          ? "bg-white text-black border-white"
                          : "border-white/30 hover:border-white"
                      }`}
                    >
                      <link.icon size={18} />
                      {t(link.labelKey)}
                    </Link>
                  );
                })}

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="bg-black text-white border-t-3 border-white">
      {/* Diagonal stripe divider */}
      <div className="h-4 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,white_10px,white_12px)]" />

      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-12 h-12 bg-white text-black flex items-center justify-center font-heading font-bold text-xl">
                PX
              </div>
              <span className="font-heading font-bold text-2xl tracking-tight">
                PLAY<span className="text-white/60">X</span>WORLD
              </span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              {t("footer.brandDescription")}
            </p>
            {/* DBD Badge - 50% smaller */}
            <div className="flex justify-start">
              <a href="https://dbdregistered.dbd.go.th/api/public/shopinfo?param=9E02C8B87C4E58F016F6AA49E01D7C5A6C24435B6FFCF43DFC02A6A4D58086A4" target="_blank" rel="noopener noreferrer">
                <img src="https://dbdregistered.dbd.go.th/api/public/banner?param=9E02C8B87C4E58F016F6AA49E01D7C5A6C24435B6FFCF43DFC02A6A4D58086A4" alt="DBD Registered" className="h-auto" style={{ maxWidth: '210px', transform: 'scale(0.7)', transformOrigin: 'left' }} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-sm tracking-wider mb-4 border-b-2 border-white pb-2">
              "QUICK LINKS"
            </h4>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-white/60 hover:text-white text-sm font-mono transition-colors"
                >
                  → {t(link.labelKey)}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-sm tracking-wider mb-4 border-b-2 border-white pb-2">
              "CONTACT"
            </h4>
            <div className="flex flex-col gap-2 text-white/60 text-sm font-mono mb-6">
              <span>LINE: @playxworld</span>
              <span>EMAIL: support@playxworld.com</span>
            </div>
            {/* QR Payment Logos */}
            <div className="flex flex-col gap-3">
              <img src="https://d2xsxph8kpxj0f.cloudfront.net/310519663229949928/ZRSsJuahteo868wQueRgPv/QR-Payment-PromptPay-Small_0b121acb.png" alt="QR Payment PromptPay" className="h-auto" style={{ maxWidth: '100%', transform: 'scale(0.7)', transformOrigin: 'left' }} />
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t-2 border-white/20 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs font-mono">
            © 2026 PlayX Co., Ltd. All rights reserved. PLAYXWORLD™ Registered trademark.
          </p>
          <div className="flex gap-4 text-white/40 text-xs font-mono">
            <Link href="/terms" className="hover:text-white transition-colors cursor-pointer">
              {t("footer.terms")}
            </Link>
            <Link href="/privacy" className="hover:text-white transition-colors cursor-pointer">
              {t("footer.privacy")}
            </Link>
            <Link href="/refund" className="hover:text-white transition-colors cursor-pointer">
              {t("footer.refund")}
            </Link>
          </div>
        </div>


      </div>
    </footer>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
